/**
 * A rule's action, picked the Flow Optimizer's way (user, 12/10/2026: "the
 * same drop down for the actions like the flow optimiser — much cleaner"):
 * its trigger (the chosen action as a tinted chip) and its items (icon, name,
 * what it does on this surface) are the Flow Optimizer's own parts. A rule
 * here has ONE action, so picking one replaces the other and closes the menu.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { RuleActionItem, RuleActionsTrigger } from '@/features/flow-optimizer/tabs/exclude/actions';
import type { RuleActionLook } from '@/features/flow-optimizer/tabs/exclude/rule-action-meta';

import { actionMeaning, EXCLUDE_LOOK, RULE_ACTIONS, RULE_SURFACES, type RuleActionTone, type RuleSurface } from '../rule-surfaces';

/** The Flow Optimizer's tints: red takes away, amber holds back, green favours. */
const TONE_CLASS: Record<RuleActionTone, string> = {
  destructive: 'bg-destructive/10 text-destructive',
  warning: 'bg-warning/10 text-warning',
  success: 'bg-success/10 text-success',
};

const lookOf = (action: string): RuleActionLook => {
  const meta = RULE_ACTIONS[action];
  if (action === 'exclude') return { value: action, label: meta.label, Icon: EXCLUDE_LOOK.Icon, tone: EXCLUDE_LOOK.tone };
  return { value: action, label: meta.label, Icon: meta.icon, tone: TONE_CLASS[meta.tone] };
};

interface Props {
  surface: RuleSurface;
  value: string;
  onChange: (action: string) => void;
  /** Actions this rule cannot take, with the reason the item shows (the Flow Optimizer's disabled items). */
  blocked?: Partial<Record<string, string>>;
}

export const RuleActionPicker: FC<Props> = ({ surface, value, onChange, blocked }) => {
  const [open, setOpen] = useState(false);
  const options = RULE_SURFACES[surface].actions
    .filter((action) => surface === 'routeTest' || action !== 'use_rest_of_day')
    .map(lookOf);
  const chosen = options.filter((o) => o.value === value);
  const pick = (action: string) => {
    if (action !== value) onChange(action);
    setOpen(false);
  };
  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <RuleActionsTrigger chosen={chosen} summary={chosen.map((o) => o.label).join('')} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="z-[100] w-[22rem] max-w-[90vw] rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg"
        >
          {options.map((o) => (
            <RuleActionItem
              key={o.value}
              action={o}
              checked={o.value === value}
              disabled={Boolean(blocked?.[o.value])}
              hint={blocked?.[o.value] ?? actionMeaning(surface, o.value)}
              onToggle={() => pick(o.value)}
            />
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
